import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

const UTM_SESSION_KEY = 'quiz_utm_params';

export function saveUtmParams() {
  const params = new URLSearchParams(window.location.search);
  const tracking: Record<string, string> = {};
  params.forEach((value, key) => {
    tracking[key] = value;
  });
  if (Object.keys(tracking).length > 0) {
    sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(tracking));
  } else {
    sessionStorage.removeItem(UTM_SESSION_KEY);
  }
}

export function getStoredUtmParams(): Record<string, string> {
  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
  };
}

export function getDeviceType() {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

export function useQuizTracking() {
  const sessionIdRef = useRef(generateSessionId());
  const startTimeRef = useRef(Date.now());
  const screenStartTimeRef = useRef(Date.now());
  const screenTimesRef = useRef<Record<string, number>>({});
  const backClicksRef = useRef(0);
  const scrollDepthRef = useRef(0);

  // Heartbeat every 2 minutes (was 30s — reduces UPDATE write load on quiz_sessions
  // by 4x and prevents flooding all dashboard realtime subscribers)
  useEffect(() => {
    const interval = setInterval(() => {
      supabase.from('quiz_sessions').update({ updated_at: new Date().toISOString() })
        .eq('session_id', sessionIdRef.current)
        .then(() => {});
    }, 120_000);
    return () => clearInterval(interval);
  }, []);

  const recordScreenTime = useCallback((screenIdx: number) => {
    const elapsed = Math.round((Date.now() - screenStartTimeRef.current) / 1000);
    screenTimesRef.current[`screen_${screenIdx}`] = (screenTimesRef.current[`screen_${screenIdx}`] || 0) + elapsed;
    screenStartTimeRef.current = Date.now();
  }, []);

  const trackScrollDepth = useCallback((currentScreen: number, totalScreens: number) => {
    if (currentScreen !== totalScreens - 1) return () => {};
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const depth = Math.round((scrollTop / docHeight) * 100);
        if (depth > scrollDepthRef.current) scrollDepthRef.current = depth;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const updateSessionStep = useCallback((nextScreen: number) => {
    supabase.from('quiz_sessions').update({
      current_step: nextScreen + 1,
      updated_at: new Date().toISOString(),
      total_duration_sec: Math.round((Date.now() - startTimeRef.current) / 1000),
      back_clicks: backClicksRef.current,
      screen_times: screenTimesRef.current,
    }).eq('session_id', sessionIdRef.current).then(() => {});
  }, []);

  const markCompleted = useCallback(() => {
    supabase.from('quiz_sessions').update({
      is_completed: true,
      updated_at: new Date().toISOString(),
      total_duration_sec: Math.round((Date.now() - startTimeRef.current) / 1000),
      back_clicks: backClicksRef.current,
      scroll_depth: scrollDepthRef.current,
      screen_times: screenTimesRef.current,
    }).eq('session_id', sessionIdRef.current).then(() => {});
  }, []);

  const incrementBackClicks = useCallback(() => {
    backClicksRef.current++;
  }, []);

  return {
    sessionIdRef,
    startTimeRef,
    screenTimesRef,
    backClicksRef,
    scrollDepthRef,
    recordScreenTime,
    trackScrollDepth,
    updateSessionStep,
    markCompleted,
    incrementBackClicks,
  };
}
